/**
 * Content Library (`/content`) — de kaartenrij per model, zoals de huidige
 * pagina. Eén endpoint: /api/admin/content-library/models.
 *
 * Elke kaart toont het totaal aantal media-items en hoeveel daarvan al
 * verwerkt zijn; de kaart "All Models" bovenaan telt alles op.
 */
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Avatar, ErrorNote, Loading, Plat, Seg, Table } from '../components/ui'
import { useApi } from '../lib/useApi'
import { num } from '../lib/format'
import ContentLibrary from './ContentLibrary'
import { Modal } from '../components/Modal'
import { ModelNaam } from '../components/ModelNaam'
import { isVolledigBeheer, useMij } from '../lib/rechten'
import { useActions, mut } from '../lib/actions'

interface LibModel {
  id: number
  name: string
  avatar_url: string | null
  stage_name: string | null
  slug: string
  fancentro_model_id: string | null
  f2f_model_id: string | null
  total_content: number
  processed: number
  unprocessed: number
  content_library_group: string | null
  /** Alle personas die deze bibliotheek delen; de items hangen soms aan een
   *  andere persona dan die op de kaart staat. */
  persona_ids?: number[]
}

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]

/**
 * Eén kaart per model, niet per platform.
 *
 * De API geeft 28 rijen: sommige modellen staan er twee keer in (een
 * FC-persona en een F2F-persona met dezelfde bibliotheek), en sommige delen
 * hun bibliotheek met een ánder model. Dat laatste staat in
 * `content_library_group`: Natalia deelt met Nathaliiia, Haselint met Amanda,
 * Irisxlz met Lana. In zo'n groep staat het aantal maar bij één van de twee,
 * de ander toont 0 — vandaar dat Natalia hier eerst leeg leek.
 *
 * Groeperen op die sleutel (en anders op naam), en per groep het hoogste
 * aantal nemen, geeft exact wat het oude scherm toont: 20 kaarten en
 * 28.413 items.
 */
function groepeer(rows: LibModel[]): LibModel[] {
  const groepen = new Map<string, LibModel & { namen: Set<string> }>()
  for (const r of rows) {
    const sleutel = r.content_library_group || `naam:${r.stage_name || r.slug}`
    const bestaand = groepen.get(sleutel)
    if (!bestaand) {
      groepen.set(sleutel, { ...r, namen: new Set([r.stage_name || r.slug]) })
      continue
    }
    bestaand.namen.add(r.stage_name || r.slug)
    // Binnen een groep hoort één bibliotheek; het hoogste aantal is de echte.
    if (r.total_content > bestaand.total_content) {
      bestaand.total_content = r.total_content
      bestaand.processed = r.processed
      bestaand.unprocessed = r.unprocessed
    }
    if (!bestaand.avatar_url && r.avatar_url) bestaand.avatar_url = r.avatar_url
  }
  return [...groepen.values()].sort((a, b) =>
    String(a.stage_name || a.slug).localeCompare(String(b.stage_name || b.slug), 'nl'))
}

export default function Content() {
  const q = useApi<LibModel[]>('/api/admin/content-library/models')
  const [platform, setPlatform] = useState('all')
  // Het gekozen model staat in de URL, net als bij oud: dan is een bibliotheek
  // te delen en werkt de terugknop van de browser.
  const [params, setParams] = useSearchParams()
  // Welke opruimactie er is aangeklikt; null = geen dialoog open.
  const [opruimen, setOpruimen] = useState<'preview' | 'clean' | null>(null)
  const [badges, setBadges] = useState(false)
  // Samenvoegen/scheiden zoals oud (23-10-2026, fase 1): alleen owner/admin.
  // Raakt alleen persona_config.content_library_group; geen bestanden, mappen
  // of platformkoppelingen (zie POST /content-library/models/group).
  const mij = useMij()
  const admin = isVolledigBeheer(mij.data)
  const { run, busy } = useActions()
  const [badgeKeus, setBadgeKeus] = useState<Set<number>>(new Set())
  const gekozenId = Number(params.get('model') || 0) || null

  const rows = useMemo(() => {
    // Eerst filteren op platform, dán groeperen: anders zou een gedeelde
    // bibliotheek onder het verkeerde platform kunnen wegvallen.
    const all = (q.data ?? []).filter((m) =>
      platform === 'all' ? true
      : platform === 'fancentro' ? !!m.fancentro_model_id
      : !!m.f2f_model_id)
    return groepeer(all)
  }, [q.data, platform])

  const totals = useMemo(() => {
    return rows.reduce(
      (a, m) => ({
        total: a.total + (m.total_content || 0),
        processed: a.processed + (m.processed || 0),
        unprocessed: a.unprocessed + (m.unprocessed || 0),
      }),
      { total: 0, processed: 0, unprocessed: 0 },
    )
  }, [rows])

  const gekozen = useMemo(
    () => rows.find((m) => m.id === gekozenId
      || (m.persona_ids ?? []).includes(gekozenId ?? -1)) ?? null,
    [rows, gekozenId])

  if (gekozenId && gekozen) {
    const namen = [...((gekozen as LibModel & { namen?: Set<string> }).namen
      ?? new Set([gekozen.stage_name || gekozen.slug]))]
    return (
      <ContentLibrary
        modelId={gekozen.id}
        personaIds={gekozen.persona_ids ?? [gekozen.id]}
        naam={namen.join(' / ')}
        tellers={{
          total: gekozen.total_content,
          unprocessed: gekozen.unprocessed,
          processed: gekozen.processed,
        }}
        onTerug={() => setParams({})}
      />
    )
  }

  return (
    <>
      <div className="filters">
        {/* Allebei openen eerst een uitleg. "Clean" stond hier rechtstreeks op
            het verwijder-endpoint met alleen een window.confirm ervoor — één
            misklik en er gingen bestanden weg die niemand had gezien. */}
        <button className="btn ghost sm" type="button"
                onClick={() => setOpruimen('preview')}>
          Preview F2F root
        </button>
        <button className="btn ghost sm" type="button"
                onClick={() => setOpruimen('clean')}>
          Clean F2F library
        </button>
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        {/* Deed een POST op /all-models-tree — een leesroute, dus dat kon
            alleen maar mislukken. Het toont nu wat er samengevoegd IS, want
            dat is de vraag waarvoor je hier klikt. */}
        <button className="btn ghost sm" type="button"
                onClick={() => setBadges(true)}>
          Manage merged badges
        </button>
      </div>

      {badges ? (
        <Modal
          title="Merged libraries"
          desc="Which models share the same content library."
          width={560}
          onClose={() => { setBadges(false); setBadgeKeus(new Set()) }}
          footer={
            <>
              <button className="btn ghost sm" type="button"
                      onClick={() => { setBadges(false); setBadgeKeus(new Set()) }}>Close</button>
              {admin ? (
                <>
                  <button className="btn ghost sm" type="button"
                          disabled={badgeKeus.size !== 1 || busy === 'Separate'}
                          onClick={() => {
                            const m = rows.find((x) => badgeKeus.has(x.id))
                            if (!m) return
                            run({
                              label: 'Separate',
                              confirm: 'Separate this merged library? Only the grouping changes; '
                                + 'files, folders and items stay where they are.',
                              request: mut.post('/api/admin/content-library/models/group',
                                { persona_ids: m.persona_ids ?? [m.id], action: 'separate' }),
                              successText: 'Library separated.',
                              onDone: () => { setBadgeKeus(new Set()); q.refetch() },
                            })
                          }}>
                    Separate selected
                  </button>
                  <button className="btn primary sm" type="button"
                          disabled={badgeKeus.size < 2 || busy === 'Merge'}
                          onClick={() => {
                            const ids = rows.filter((x) => badgeKeus.has(x.id))
                              .flatMap((x) => x.persona_ids ?? [x.id])
                            run({
                              label: 'Merge',
                              confirm: `Merge ${badgeKeus.size} libraries into one badge? `
                                + 'Only the grouping changes; files, folders and items stay where they are.',
                              request: mut.post('/api/admin/content-library/models/group',
                                { persona_ids: ids, action: 'merge' }),
                              successText: 'Libraries merged.',
                              onDone: () => { setBadgeKeus(new Set()); q.refetch() },
                            })
                          }}>
                    Merge selected
                  </button>
                </>
              ) : null}
            </>
          }
        >
          {admin ? (
            <div style={{ marginBottom: 14 }}>
              <b style={{ fontSize: 12.5 }}>Select badges</b>
              <div className="mut2" style={{ fontSize: 12, margin: '2px 0 6px' }}>
                Two or more to merge; one merged badge to separate.
              </div>
              <div style={{ maxHeight: 260, overflowY: 'auto', display: 'grid', gap: 4 }}>
                {rows.map((m) => (
                  <label key={m.id} className="row" style={{ gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
                    <input type="checkbox" checked={badgeKeus.has(m.id)}
                           onChange={(e) => setBadgeKeus((oud) => {
                             const n = new Set(oud)
                             if (e.target.checked) n.add(m.id); else n.delete(m.id)
                             return n
                           })} />
                    <span>{[...((m as LibModel & { namen?: Set<string> }).namen
                      ?? new Set([m.stage_name || m.slug]))].join(' / ')}</span>
                    <span className="mut2">{num(m.total_content)}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <Table cols={[{ key: 'g', label: 'Group' }, { key: 'm', label: 'Models' },
                        { key: 'i', label: 'Items', num: true }]}
                 emptyText="No merged libraries.">
            {rows
              .filter((m) => ((m as LibModel & { namen?: Set<string> }).namen?.size ?? 1) > 1)
              .map((m) => (
                <tr key={m.id}>
                  <td className="mut">{m.content_library_group ?? '—'}</td>
                  <td>{[...((m as LibModel & { namen?: Set<string> }).namen
                        ?? new Set<string>())].map((n, i) => (
                          <span key={n} style={{ marginRight: 8 }}>{i ? '/ ' : ''}<ModelNaam naam={n} size={18} /></span>
                        ))}</td>
                  <td className="num mono">{num(m.total_content)}</td>
                </tr>
              ))}
          </Table>
          <div className="note" style={{ marginTop: 12 }}>
            A group hangs on <code>persona_config.content_library_group</code>.
            Items are often stored under only one of the personas, so the card
            counts the highest number within the group, not the sum — otherwise
            the same library would be counted twice.
          </div>
        </Modal>
      ) : null}

      {opruimen ? (
        <Modal
          title={opruimen === 'preview' ? 'Preview F2F root' : 'Clean F2F library'}
          desc="What this action does, before anything happens."
          width={560}
          onClose={() => setOpruimen(null)}
          footer={
            <>
              <button className="btn ghost sm" type="button"
                      onClick={() => setOpruimen(null)}>Close</button>
              <button className="btn primary sm" type="button" disabled>
                {opruimen === 'preview' ? 'Run preview' : 'Clean'}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {opruimen === 'preview'
              ? 'Walks the root of one model’s F2F library and '
                + 'lists the loose files there — files that belong to no '
                + 'post. Read-only; nothing is moved or '
                + 'deleted.'
              : 'Deletes the loose files from the root of the '
                + 'F2F library. Paid and manually placed posts '
                + 'ALWAYS stay; the cleanup works on the exact '
                + 'fc_vault_item_id combined with created_by=posting_bot, '
                + 'never on title.'}
          </p>
          <div className="note">
            <b>Not available yet.</b> This action needs the worker’s browser
            sessions, which are deliberately off tonight. The button therefore
            stays disabled until you approve it separately — a misclick in a
            cleanup cannot be undone.
          </div>
        </Modal>
      ) : null}

      {q.loading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : (
        <>
          <div
            className="grid contentpagina"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
          >
            <div
              className="modelcard"
              style={{ borderColor: 'var(--acc)', background: 'var(--panel3)' }}
            >
              <div className="top">
                <div className="logo" style={{ width: 32, height: 32, fontSize: 13 }}>▤</div>
                <div>
                  <b>All Models</b>
                  <div className="mut2" style={{ fontSize: 12 }}>overview</div>
                </div>
              </div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{num(totals.total)}</div>
              <div className="mut2" style={{ fontSize: 12 }}>items total</div>
            </div>

            {rows.map((m) => {
              const namen = [...((m as LibModel & { namen?: Set<string> }).namen
                ?? new Set([m.stage_name || m.slug]))]
              return (
              <div className="modelcard klikbaar" key={m.id} role="button" tabIndex={0}
                   onClick={() => setParams({ model: String(m.id) })}
                   onKeyDown={(e) => {
                     if (e.key === 'Enter' || e.key === ' ') {
                       e.preventDefault(); setParams({ model: String(m.id) })
                     }
                   }}>
                <div className="top">
                  <Avatar name={m.stage_name || m.slug} src={m.avatar_url} />
                  {/* `min-width:0` is hier het hele punt: zonder dat weigert een
                      flex-kind te krimpen en schoof "Amanda / Haselint" over de
                      platformchip heen. Twee namen komen nu netjes op twee
                      regels. */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b className="kaartnaam" title={namen.join(' / ')}>
                      {namen.join(' / ')}
                    </b>
                    <div className="mut2 kaartslug">{m.slug}</div>
                  </div>
                  <Plat value={m.fancentro_model_id ? 'fc' : 'f2f'} />
                </div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{num(m.total_content)}</div>
                <div className="mut2" style={{ fontSize: 12 }}>
                  {num(m.total_content)} items ·{' '}
                  <span style={{ color: 'var(--acc2)' }}>{num(m.unprocessed)} new</span>
                </div>
                <div className="progress">
                  <div
                    className="p"
                    style={{
                      width: `${Math.round(
                        (m.processed / (m.total_content || 1)) * 100,
                      )}%`,
                    }}
                  />
                </div>
              </div>
              )
            })}
          </div>

          <div className="note" style={{ marginTop: 16 }}>
            Select a model above to manage their content library, or click “All
            Models” for an overview.
          </div>
        </>
      )}
    </>
  )
}
